import React, { useMemo } from 'react';
import useRulePerformance from 'Hooks/query-hooks/reports/useRulePerformance';
import { Column, Row } from 'react-table';
import * as _ from 'lodash';
import DashletContainer from 'Components/DashletContainer';
import clsx from 'clsx';
import { ProgressBar } from '@brighterion/br-ui';
import { useTranslation, Trans } from 'react-i18next';
import RulePerformanceTable from './RulePerformanceTable';
import styles from './index.css';

const RulePerformanceDashlet = ({
    formValues,
    className,
    minColumns,
    chartType,
    sortView,
    setRejectedTransactionCount,
    setTotalTransactionCount,
    totalRejectedTxnCount,
    totalTxnCount
}: RulePerformanceProps) => {
    const { t } = useTranslation();
    const { isLoading, processedData } = useRulePerformance(chartType, sortView, formValues);
    const maxBarAccessor = chartType === 'all' ? 'percentScaledTriggeredAll' : 'percentScaledTriggeredReject';
    const maxBarValue = useMemo(() => {
        const maxObj = _.maxBy(processedData, (rulePerformanceTableItem) => rulePerformanceTableItem[maxBarAccessor]);
        return maxObj && maxObj[maxBarAccessor] ? maxObj[maxBarAccessor] : 1;
    }, [processedData, maxBarAccessor]);

    const columns: Column<RulePerformanceTableItem>[] = useMemo(() => {
        const percentColumn: Column<RulePerformanceTableItem> = {
            Header: 'Percent Rule Triggered',
            accessor: chartType === 'all' ? 'percentScaledTriggeredAll' : 'percentScaledTriggeredReject',
            renderType: 'overrideCell',
            Cell: ({ value, row }: { value: number; row: Row }) => {
                const label =
                    chartType === 'all' ? row.original.percentTriggeredAll : row.original.percentTriggeredReject;
                const labelClassName = clsx(styles.progressBarLabel, _.round(label, 2) !== 0 && styles.notZero);
                return (
                    <ProgressBar
                        className={styles.progressBarWrapper}
                        fillAreaClass={styles[`${chartType}ProgressBarFillArea`]}
                        labelClassName={labelClassName}
                        inlineLabel
                        labelPosition='back'
                        progress={_.round(Number(value) / (maxBarValue || 1), 4)}
                        labelOverride={_.round(label, 2)}
                        isTransparent
                    />
                );
            }
        };
        return [...minColumns, { ...percentColumn }];
    }, [chartType, maxBarValue, minColumns]);

    React.useEffect(() => {
        if (!isLoading && setRejectedTransactionCount) {
            setRejectedTransactionCount(totalRejectedTxnCount!);
        }
        if (!isLoading && setTotalTransactionCount) {
            setTotalTransactionCount(totalTxnCount!);
        }
    }, [isLoading, setRejectedTransactionCount, setTotalTransactionCount, totalTxnCount, totalRejectedTxnCount]);

    const dashletContainerCN = clsx(className, styles.container);
    const testId = `rule-performance-${chartType}`;

    const isRejectChart = chartType === 'reject';

    const subHeader = (
        <>
            <Trans i18nKey='rulePerformanceSubHeader'>
                Rules triggered
                {{ chartType: isRejectChart ? 'Rejected' : 'All' }}
            </Trans>
        </>
    );

    return (
        <DashletContainer
            dataTestId={testId}
            className={dashletContainerCN}
            isLoading={isLoading}
            header={{
                label: isRejectChart ? t('ruleSignificanceTitle') : t('triggerFrequencyTitle'),
                toolTip: isRejectChart ? t('ruleSignificanceTooltip') : t('triggerFrequencyTooltip')
            }}
            subHeader={subHeader}
            descriptionAboveChildren={t('transactions', {
                number: setTotalTransactionCount ? totalTxnCount : totalRejectedTxnCount
            })}
        >
            <RulePerformanceTable data={processedData} columns={columns} dataTestId={`${testId}-table`} />
        </DashletContainer>
    );
};

export default RulePerformanceDashlet;
